import { useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import {
  DISTRIBUTION_TITLE_LABEL,
  PER_SHARE_TITLE_LABEL,
  WATERFALL_CALCULATOR_TABLE_NAME,
  WATERFALL_CALCULATOR_TABLE_SLUG,
} from 'dashboard409a/common/constants/components/waterfallTable';
import { IGetCaptableResponse, Security } from 'dashboard409a/services/types';
import parser from '../config/parser';
import rowConfig from '../config/rowConfig';
import { Column } from '../config/types';
import { UseWaterfallTableDataProps } from '../types';

const getData = (exitEquityValue: string, data: IGetCaptableResponse | null) => {
  if (!data) {
    return [];
  }

  const { securities } = data;

  const exitEquityValueNumber = Number.parseFloat(exitEquityValue);
  const total = securities.reduce((prev, current) => prev + Number.parseFloat(current.total_preference), 0);

  const result = securities.map(security => {
    const totalPreference = Number(security.total_preference);
    const adjustedTotal = total > 0 ? ((exitEquityValueNumber - total) / total) * totalPreference + totalPreference : totalPreference;
    
    return {
      ...security,
      total_preference: exitEquityValueNumber > 0 ? adjustedTotal.toString() : totalPreference,
    };
  }) as Security[];

  return result;
};

const useWaterfallTableData = ({ exitEquityValue, data, format }: UseWaterfallTableDataProps) => {
  const [securities, setSecurities] = useState<Security[]>([]);
  const [columns, setColumns] = useState<Column[]>();
  const [spreadsheet, setSpreadsheet] = useState<SpreadsheetConfig>();
  const { prices_per_share: pricesPerShare } = data?.waterfall_breakpoints || {};

  useEffect(() => {
    const loadData = async () => {
      const response = getData(exitEquityValue, data);
      setSecurities(response);
    };
    loadData();
  }, [exitEquityValue, data]);

  const config = useMemo(() => rowConfig({ securities }), [securities]);

  useEffect(() => {
    if (!isEmpty(securities) && pricesPerShare) {
      let perShareObj = {};

      const securitiesObj = securities.reduce(
        (prev, current) => ({ ...prev, [`security-${current.id}`]: current.total_preference }),
        {}
      );

      Object.keys(securitiesObj).forEach((security, index) => {
        perShareObj = {
          ...perShareObj,
          [security]: pricesPerShare[index],
        };
      });
      setColumns([securitiesObj, perShareObj]);
    }
  }, [pricesPerShare, securities]);

  const tableTerms: any = useMemo(
    () => [
      {
        tableName: WATERFALL_CALCULATOR_TABLE_NAME,
        tableSlug: WATERFALL_CALCULATOR_TABLE_SLUG,
        columnName: DISTRIBUTION_TITLE_LABEL,
      },
      {
        tableName: WATERFALL_CALCULATOR_TABLE_NAME,
        tableSlug: WATERFALL_CALCULATOR_TABLE_SLUG,
        columnName: PER_SHARE_TITLE_LABEL,
      },
    ],
    []
  );

  useEffect(() => {
    if (columns && tableTerms) {
      const spreadsheetConfig = new SpreadsheetConfig({
        parser,
        rowConfig: config,
        name: 'WaterfallCalculator',
        columns,
        tableData: { columns },
        tableTerms,
        showTotalColumn: false,
        showToolbar: true,
        showPreviousColsDivider: true,
        hasColTitle: true,
        allowConfirmAndDeleteColumn: false,
        currencyFormatter: false,
        unitsFormatter: false,
        totalParser: undefined,
        alwaysDisplayLegend: false,
        format,
      });

      setSpreadsheet(spreadsheetConfig);
    }
  }, [columns, config, format, tableTerms]);

  return {
    spreadsheet,
  };
};

export default useWaterfallTableData;
